import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../shared/contexts/AuthContext';
import { socialMediaService } from '../../../shared/services/socialMediaService';
import { supabase } from '../../../shared/lib/supabase';
import Toast from '../../../shared/components/ui/Toast';

interface ToastState {
  isVisible: boolean;
  type: 'success' | 'error' | 'info';
  message: string;
}

const ParentSocialMediaSettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);
  const [parentIntegrations, setParentIntegrations] = useState<any[]>([]);
  const [toast, setToast] = useState<ToastState>({
    isVisible: false,
    type: 'info',
    message: '',
  });

  // Check if user is super admin
  useEffect(() => {
    const checkSuperAdmin = async () => {
      if (!user) {
        setIsLoading(false);
        return;
      }

      try {
        const { data: userData, error } = await supabase
          .from('users')
          .select('global_role')
          .eq('id', user.id)
          .single();

        if (error) {
          console.error('Error checking admin status:', error);
          setIsLoading(false);
          return;
        }

        const superAdmin = userData?.global_role === 'super_admin';
        setIsSuperAdmin(superAdmin);
        setIsLoading(false);

        if (superAdmin) {
          loadParentIntegrations();
        }
      } catch (error) {
        console.error('Error in checkSuperAdmin:', error);
        setIsLoading(false);
      }
    };

    checkSuperAdmin();
  }, [user]);

  // Load parent-level integrations
  const loadParentIntegrations = async () => {
    try {
      const platforms = ['youtube', 'instagram', 'tiktok', 'facebook', 'twitter'];
      const integrations: any[] = [];

      for (const platform of platforms) {
        const result = await socialMediaService.getParentIntegration(platform);
        if (result.success && result.data) {
          integrations.push(result.data);
        }
      }

      setParentIntegrations(integrations);
    } catch (error) {
      console.error('Error loading parent integrations:', error);
    }
  };

  // Handle OAuth callback (check if we're returning from OAuth)
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const code = urlParams.get('code');
    const error = urlParams.get('error');

    if (error) {
      setToast({
        isVisible: true,
        type: 'error',
        message: `OAuth error: ${error}`,
      });
      // Clean URL
      navigate(location.pathname, { replace: true });
      return;
    }

    if (code) {
      // OAuth callback - the edge function handles the rest
      // Just show success message and reload integrations
      setToast({
        isVisible: true,
        type: 'success',
        message: 'Account connected successfully!',
      });
      loadParentIntegrations();
      // Clean URL
      navigate(location.pathname, { replace: true });
    }
  }, [location.search, location.pathname, navigate]);

  const handleConnectYouTube = async () => {
    if (isConnecting) return;

    setIsConnecting(true);
    try {
      const session = await supabase.auth.getSession();
      if (!session.data.session) {
        throw new Error('Not authenticated');
      }

      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
      const redirectUri = `${window.location.origin}${location.pathname}`;
      
      // Initiate OAuth flow for parent-level account
      const response = await fetch(`${supabaseUrl}/functions/v1/youtube-oauth-callback?action=initiate&redirect_uri=${encodeURIComponent(redirectUri)}&state=parent`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${session.data.session.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to initiate OAuth');
      }

      const data = await response.json();
      if (data.oauthUrl) {
        window.location.href = data.oauthUrl;
      } else {
        throw new Error('No OAuth URL returned from server');
      }
    } catch (error) {
      console.error('Error connecting YouTube:', error);
      setToast({
        isVisible: true,
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to connect YouTube account',
      });
      setIsConnecting(false);
    }
  };

  const handleReconnectYouTube = async (integrationId: string) => {
    await handleConnectYouTube();
  };

  const handleManualRefresh = async (integrationId: string) => {
    try {
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
      const session = await supabase.auth.getSession();
      if (!session.data.session) {
        throw new Error('Not authenticated');
      }

      setToast({
        isVisible: true,
        type: 'info',
        message: 'Refreshing token...',
      });

      // Call refresh endpoint manually
      const response = await fetch(`${supabaseUrl}/functions/v1/refresh-social-tokens`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.data.session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ integrationId }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to refresh token');
      }

      const result = await response.json();
      if (result.success) {
        setToast({
          isVisible: true,
          type: 'success',
          message: 'Token refreshed successfully!',
        });
        loadParentIntegrations();
      } else {
        throw new Error(result.error || 'Failed to refresh token');
      }
    } catch (error) {
      console.error('Error refreshing token:', error);
      setToast({
        isVisible: true,
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to refresh token',
      });
    }
  };

  const handleDisconnectYouTube = async (integrationId: string) => {
    if (!window.confirm('Are you sure you want to disconnect this account? This will prevent uploads from using it as a fallback.')) {
      return;
    }

    try {
      // For parent-level, we need to delete directly from database
      const { error } = await supabase
        .from('social_media_integrations')
        .delete()
        .eq('id', integrationId)
        .eq('parent_level', true);
      
      if (error) {
        throw error;
      }

      setToast({
        isVisible: true,
        type: 'success',
        message: 'Account disconnected successfully',
      });
      loadParentIntegrations();
    } catch (error) {
      console.error('Error disconnecting YouTube:', error);
      setToast({
        isVisible: true,
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to disconnect account',
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (!isSuperAdmin) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">Only super admins can manage parent-level social media integrations.</p>
        </div>
      </div>
    );
  }

  const youtubeIntegration = parentIntegrations.find(i => i.platform === 'youtube');

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Parent-Level Social Media Settings</h1>
        <p className="text-gray-600">
          Manage Kridah Labs parent-level social media integrations. These accounts are used as fallback when organizations don't have their own integrations.
        </p>
      </div>

      {/* YouTube Integration */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
              <svg className="w-8 h-8 text-red-600" fill="currentColor" viewBox="0 0 24 24">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
              </svg>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">YouTube</h2>
              <p className="text-sm text-gray-500">Parent-level YouTube account</p>
            </div>
          </div>
          <span className="px-3 py-1 bg-green-100 text-green-800 text-sm font-medium rounded-full">
            Available
          </span>
        </div>

        {youtubeIntegration ? (
          <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">{youtubeIntegration.platform_account_name}</h3>
                <p className="text-sm text-gray-600 mb-2">Channel ID: {youtubeIntegration.platform_account_id}</p>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                  <span className={`px-2 py-1 rounded ${
                    youtubeIntegration.is_active 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {youtubeIntegration.is_active ? '✓ Active' : '✗ Inactive'}
                  </span>
                  {/* Access token expiry is not shown (short-lived, causes fake urgency). Google does not provide refresh token expiry. */}
                  <span className="text-gray-600">
                    Access token refreshes automatically when needed.
                  </span>
                </div>
              </div>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => handleManualRefresh(youtubeIntegration.id)}
                className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700"
                title="Manually refresh the access token"
              >
                Refresh Token
              </button>
              <button
                onClick={() => handleReconnectYouTube(youtubeIntegration.id)}
                disabled={isConnecting}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isConnecting ? 'Connecting...' : 'Reconnect'}
              </button>
              <button
                onClick={() => handleDisconnectYouTube(youtubeIntegration.id)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Disconnect
              </button>
            </div>
          </div>
        ) : (
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
            <div className="mb-4">
              <svg className="w-16 h-16 text-gray-400 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
            </div>
            <p className="text-gray-600 mb-4">No parent-level YouTube account connected</p>
            <button
              onClick={handleConnectYouTube}
              disabled={isConnecting}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isConnecting ? 'Connecting...' : 'Connect YouTube Account'}
            </button>
          </div>
        )}
      </div>

      {/* Coming Soon Section */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Coming Soon</h2>
        <p className="text-gray-600 mb-4">
          We're working on adding support for more social media platforms. Stay tuned!
        </p>
        <div className="flex space-x-4">
          {['instagram', 'tiktok', 'facebook', 'twitter'].map((platform) => (
            <div
              key={platform}
              className="flex-1 border border-gray-200 rounded-lg p-4 text-center opacity-50 cursor-not-allowed"
            >
              <div className="text-2xl mb-2">
                {platform === 'instagram' && '📷'}
                {platform === 'tiktok' && '🎵'}
                {platform === 'facebook' && '📘'}
                {platform === 'twitter' && '🐦'}
              </div>
              <div className="text-sm font-medium text-gray-600 capitalize">{platform}</div>
            </div>
          ))}
        </div>
      </div>

      <Toast
        isVisible={toast.isVisible}
        type={toast.type}
        message={toast.message}
        onClose={() => setToast({ ...toast, isVisible: false })}
      />
    </div>
  );
};

export default ParentSocialMediaSettingsPage;
